import React from 'react';
import { Clock, Shield, BarChart3, Users } from 'lucide-react';
import { fundDetails } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

export const FundDetails = () => {
  const details = [
    {
      icon: Shield,
      title: "Fund Structure",
      value: fundDetails.structure,
      description: "Regulatory compliance under SEBI AIF norms"
    },
    {
      icon: Clock,
      title: "Fund Tenure",
      value: fundDetails.tenure,
      description: "Long-term investment horizon"
    },
    {
      icon: BarChart3,
      title: "Management Fee",
      value: fundDetails.managementFee,
      description: "Charged quarterly in arrears"
    },
    {
      icon: Users,
      title: "Minimum Lock-in",
      value: fundDetails.lockIn,
      description: "Post deployment period"
    }
  ];

  const faqData = [
    {
      question: "What is the minimum investment amount?",
      answer: "The minimum investment commitment is ₹1 crore, designed for Qualified Institutional Buyers (QIBs) and High Net Worth Individuals (HNIs) as per SEBI guidelines."
    },
    {
      question: "How often will I receive updates on my investment?",
      answer: "We provide detailed quarterly reports including portfolio updates, NAV statements, and market outlook. Additionally, annual audits are conducted by independent auditors."
    },
    {
      question: "What is the fund's investment focus?",
      answer: "Our fund focuses on anchor investing in Mainboard IPOs, SME IPO participation, pre-IPO rounds, and early-stage startups with listing potential within 12-36 months."
    },
    {
      question: "What are the key risks involved?",
      answer: "Investments carry moderate to high risk due to market volatility, lock-in periods, and the nature of primary market investments. Investors should understand and accept these risks before investing."
    },
    {
      question: "How is the fund regulated?",
      answer: "Credora Capital operates as a Category I AIF under SEBI (Alternative Investment Funds) Regulations, 2012, ensuring full regulatory compliance and investor protection."
    }
  ];

  return (
    <section id="fund-details" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Fund <span className="text-emerald-600">Details</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive information about our fund structure, fees, and investment terms
          </p>
        </div>

        {/* Fund Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {details.map((detail, index) => {
            const IconComponent = detail.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 border border-gray-200 hover:border-emerald-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{detail.title}</h3>
                <div className="text-2xl font-bold text-emerald-600 mb-2">{detail.value}</div>
                <p className="text-sm text-gray-600">{detail.description}</p>
              </div>
            );
          })}
        </div>

        {/* Additional Fund Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Terms</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-emerald-200">
                <span className="font-semibold text-gray-900">Hurdle Rate</span>
                <span className="text-emerald-600 font-bold">{fundDetails.hurdleRate}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-emerald-200">
                <span className="font-semibold text-gray-900">Reporting Frequency</span>
                <span className="text-emerald-600 font-bold">Quarterly</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-emerald-200">
                <span className="font-semibold text-gray-900">Valuation Method</span>
                <span className="text-emerald-600 font-bold">SEBI Guidelines</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="font-semibold text-gray-900">Audit</span>
                <span className="text-emerald-600 font-bold">Annual Independent</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Investment Process</h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Initial Consultation</h4>
                  <p className="text-sm text-gray-600">Discuss investment objectives and eligibility</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Due Diligence</h4>
                  <p className="text-sm text-gray-600">KYC verification and documentation</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Investment Agreement</h4>
                  <p className="text-sm text-gray-600">Finalize terms and commitment</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Capital Deployment</h4>
                  <p className="text-sm text-gray-600">Strategic investment execution</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-10">
            Frequently Asked Questions
          </h3>
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border border-gray-200 rounded-2xl px-6 py-2"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-600 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};